// pattern: imperative-shell

import { relativeDate } from "../core/date-utils";
import { formatTemp, weatherEmoji } from "../core/weather-model";
import type { ApiObservation, ApiWeather } from "../shell/api-client";
import { api } from "../shell/api-client";
import { renderObservationCard } from "./observation-card";

export async function renderTimeline(container: HTMLElement): Promise<void> {
	container.innerHTML = '<div class="loading">Loading...</div>';

	try {
		const observations = await api.getObservations({ limit: "50" });

		if (observations.length === 0) {
			container.innerHTML = `
				<div class="empty">
					<div class="empty-icon">\u{1F331}</div>
					<div class="empty-text">No observations yet</div>
					<button class="btn btn-primary" id="empty-add">Add your first observation</button>
				</div>
			`;
			container.querySelector("#empty-add")?.addEventListener("click", () => {
				window.location.hash = "#/new";
			});
			return;
		}

		// Group by date and fetch weather for the date range
		const groups = groupByDate(observations);
		const dates = groups.map(([d]) => d);
		const from = dates[dates.length - 1]!;
		const to = dates[0]!;

		// Fetch weather in parallel (non-blocking — render without it if it fails)
		let weatherMap = new Map<string, ApiWeather>();
		try {
			const weather = await api.getWeather(from, to);
			weatherMap = new Map(weather.map((w) => [w.date, w]));
		} catch {
			// Weather data unavailable — continue without it
		}

		container.innerHTML = "";
		const content = document.createElement("div");
		content.className = "content";

		for (const [date, obs] of groups) {
			const header = document.createElement("div");
			header.className = "date-header";

			const weather = weatherMap.get(date);
			if (weather) {
				const emoji = weatherEmoji(weather.conditions);
				const highLow =
					weather.temp_high_c !== null && weather.temp_low_c !== null
						? `${formatTemp(weather.temp_high_c)}`
						: "";
				const precip =
					weather.precip_mm !== null && weather.precip_mm > 0
						? ` \u{1F4A7}${weather.precip_mm.toFixed(1)}mm`
						: "";

				header.innerHTML = `
					<span>${relativeDate(date)}</span>
					<span class="weather-badge">${emoji} ${highLow}${precip}</span>
				`;
			} else {
				header.textContent = relativeDate(date);
			}

			content.appendChild(header);

			for (const o of obs) {
				content.appendChild(renderObservationCard(o));
			}
		}

		container.appendChild(content);
	} catch (e) {
		container.innerHTML = `<div class="content"><div class="error">Failed to load observations: ${e instanceof Error ? e.message : "unknown error"}</div></div>`;
	}
}

function groupByDate(observations: ApiObservation[]): Array<[string, ApiObservation[]]> {
	const map = new Map<string, ApiObservation[]>();
	for (const obs of observations) {
		const existing = map.get(obs.observed_date);
		if (existing) {
			existing.push(obs);
		} else {
			map.set(obs.observed_date, [obs]);
		}
	}
	return [...map.entries()];
}
